import { Box, IconButton, TextField, Typography, Paper } from "@mui/material";
import React, { ChangeEvent, forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import CachedIcon from "@mui/icons-material/Cached";

export interface ICaptchaRef {
	checkCaptcha: () => boolean;
	resetCaptcha: () => void;
}

const CAPTCHA_LENGTH = 4;
const CHAR_CODES = [
	[65, 90],
	[97, 122],
	[48, 57]
];

const Captcha = forwardRef<ICaptchaRef>((_props, ref) => {
	const captchaCanvasRef = useRef<HTMLCanvasElement>(null);

	const [captchaText, setCaptchaText] = useState<string>("");
	const [input, setInput] = useState<string>("");
	const [captchaError, setCaptchaError] = useState<string>("");

	function generateRandomCharacters(min: number, max: number) {
		return String.fromCharCode(Math.floor(Math.random() * (max - min + 1) + min));
	}

	function generateCaptchaText() {
		let captcha = "";

		for (let i = 0; i < CAPTCHA_LENGTH; i++) {
			const selectedCodeRange = CHAR_CODES[Math.floor(Math.random() * 3)];
			captcha += generateRandomCharacters(selectedCodeRange[0], selectedCodeRange[1]);
		}

		return captcha;
	}

	function renderCaptcha(captcha: string) {
		if (!captchaCanvasRef.current) return;

		const canvasContext = captchaCanvasRef.current.getContext("2d");
		if (!canvasContext) return;

		canvasContext.clearRect(0, 0, canvasContext.canvas.width, canvasContext.canvas.height);

		// Add subtle noise/dots
		canvasContext.fillStyle = "rgba(102, 126, 234, 0.1)";
		for (let i = 0; i < 50; i++) {
			const x = Math.random() * captchaCanvasRef.current.width;
			const y = Math.random() * captchaCanvasRef.current.height;
			canvasContext.beginPath();
			canvasContext.arc(x, y, 1, 0, 2 * Math.PI);
			canvasContext.fill();
		}

		const textColors = ["#2c3e50", "#34495e", "#7f8c8d", "#95a5a6", "#2c3e50", "#34495e"];
		const letterSpace = 120 / captcha.length;

		for (let i = 0; i < captcha.length; i++) {
			const xInitialSpace = 20;

			canvasContext.font = "20px Roboto Mono";
			canvasContext.fillStyle = textColors[Math.floor(Math.random() * textColors.length)];

			// Add text shadow for better readability
			canvasContext.shadowColor = "rgba(0, 0, 0, 0.1)";
			canvasContext.shadowBlur = 2;
			canvasContext.shadowOffsetX = 1;
			canvasContext.shadowOffsetY = 1;

			canvasContext.fillText(captcha[i], xInitialSpace + i * letterSpace, Math.floor(Math.random() * 16 + 25), 100);
		}
	}

	function initializeCaptcha() {
		if (!captchaCanvasRef.current) return;

		const canvasContext = captchaCanvasRef.current.getContext("2d");
		if (!canvasContext) return;

		setInput("");
		setCaptchaError("");

		const newCaptcha = generateCaptchaText();
		setCaptchaText(newCaptcha);
		renderCaptcha(newCaptcha);
	}

	function handleChangeInput(event: ChangeEvent<HTMLInputElement>) {
		setInput(event.target.value);
		if (captchaError) {
			setCaptchaError("");
		}
	}

	function handleSubmitCaptcha() {
		if (input === captchaText) {
			setCaptchaError("");
			return true;
		}

		initializeCaptcha();
		setCaptchaError("Invalid captcha code");
		return false;
	}

	useEffect(() => {
		initializeCaptcha();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useImperativeHandle(ref, () => ({
		checkCaptcha: handleSubmitCaptcha,
		resetCaptcha: initializeCaptcha
	}));

	return (
		<Box>
			<Typography fontWeight={600} color="text.primary" marginBottom="0.5rem">
				Security Verification
			</Typography>

			<Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
				<Paper elevation={0} classes={{ root: "captcha-paper-root" }}>
					<canvas ref={captchaCanvasRef} width={180} height={56} style={{ display: "block" }} />

					<IconButton onClick={initializeCaptcha} tabIndex={-1} classes={{ root: "captcha-reset-button" }}>
						<CachedIcon color="primary" fontSize="inherit" />
					</IconButton>
				</Paper>

				<TextField
					variant="outlined"
					placeholder="Enter the code"
					autoComplete="off"
					value={input}
					onChange={handleChangeInput}
					error={!!captchaError}
					helperText={captchaError}
					sx={{ flex: 1 }}
				/>
			</Box>
		</Box>
	);
});

export default Captcha;
